import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AppLayout } from "@/components/AppLayout";
import { apiGetFieldPermissions, apiUpdateFieldPermissions, type FieldPermission, type UpdateFieldPermissionPayload } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Loader2, Shield, KeyRound, Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { TableSkeleton } from "@/components/PageSkeleton";

const SECTION_ORDER = [
  "public_profile",
  "hr_info",
  "birthday_hire",
  "personal_info",
  "bank_details",
  "emergency_contact",
  "children",
  "qualifications",
  "equipment",
  "visa",
  "salary",
];

const SECTION_I18N: Record<string, string> = {
  public_profile: "profile.sections.publicProfile",
  hr_info: "profile.sections.hrInfo",
  birthday_hire: "profile.sections.birthdayHire",
  personal_info: "profile.sections.personalInfo",
  bank_details: "profile.sections.bankDetails",
  emergency_contact: "profile.sections.emergencyContact",
  children: "profile.sections.children",
  qualifications: "profile.sections.qualifications",
  equipment: "profile.sections.equipment",
  visa: "profile.sections.visa",
  salary: "profile.tabs.salary",
};

export default function FieldPermissionsSettings() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: permissions = [], isLoading } = useQuery({
    queryKey: ["field-permissions"],
    queryFn: apiGetFieldPermissions,
  });

  const [localPerms, setLocalPerms] = useState<FieldPermission[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setLocalPerms(permissions);
    setHasChanges(false);
  }, [permissions]);

  const mutation = useMutation({
    mutationFn: (data: UpdateFieldPermissionPayload[]) => apiUpdateFieldPermissions(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["field-permissions"] });
      toast({ title: t("fieldPermissions.saveSuccess"), description: t("fieldPermissions.saveSuccessDesc") });
      setHasChanges(false);
    },
    onError: () => {
      toast({ title: t("fieldPermissions.saveError"), description: t("fieldPermissions.saveErrorDesc"), variant: "destructive" });
    },
  });

  const togglePerm = (fieldName: string, column: "editableByUser" | "editableByHr" | "editableByLineManager" | "editableByAdmin") => {
    setLocalPerms((prev) =>
      prev.map((p) =>
        p.fieldName === fieldName ? { ...p, [column]: !p[column] } : p
      )
    );
    setHasChanges(true);
  };

  const handleSave = () => {
    const payload: UpdateFieldPermissionPayload[] = localPerms.map((p) => ({
      fieldName: p.fieldName,
      editableByUser: p.editableByUser,
      editableByHr: p.editableByHr,
      editableByLineManager: p.editableByLineManager,
      editableByAdmin: p.editableByAdmin,
    }));
    mutation.mutate(payload);
  };

  const groupedBySections = SECTION_ORDER.map((section) => ({
    section,
    fields: localPerms.filter((p) => p.section === section),
  })).filter((g) => g.fields.length > 0);

  return (
    <AppLayout>
      <div className="w-full space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-slate-500/10 flex items-center justify-center shrink-0">
              <KeyRound className="h-5 w-5 text-slate-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {t("fieldPermissions.title")}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">{t("fieldPermissions.subtitle")}</p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={!hasChanges || mutation.isPending}>
            {mutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {mutation.isPending ? t("common.saving") : t("common.save")}
          </Button>
        </div>

        {isLoading ? (
          <div className="space-y-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <Card key={i} className="shadow-card">
                <CardHeader className="pb-2">
                  <Skeleton className="h-5 w-32" />
                </CardHeader>
                <CardContent>
                  <TableSkeleton rows={4} cols={5} />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
        <>
        {/* Permissions table grouped by section */}
        {groupedBySections.map(({ section, fields }) => (
          <Card key={section} className="shadow-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">
                <Badge variant="secondary" className="text-xs">
                  {t(SECTION_I18N[section] || section)}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground w-1/3">{t("fieldPermissions.fieldName")}</th>
                      <th className="text-center py-2 px-2 font-medium text-muted-foreground w-[15%]">{t("fieldPermissions.user")}</th>
                      <th className="text-center py-2 px-2 font-medium text-muted-foreground w-[15%]">{t("fieldPermissions.hrManager")}</th>
                      <th className="text-center py-2 px-2 font-medium text-muted-foreground w-[15%]">{t("fieldPermissions.lineManager")}</th>
                      <th className="text-center py-2 px-2 font-medium text-muted-foreground w-[15%]">{t("fieldPermissions.admin")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map((perm) => (
                      <tr key={perm.fieldName} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="py-2.5 px-2 font-mono text-xs text-foreground">{perm.fieldName}</td>
                        <td className="py-2.5 px-2 text-center">
                          <Checkbox
                            checked={perm.editableByUser}
                            onCheckedChange={() => togglePerm(perm.fieldName, "editableByUser")}
                          />
                        </td>
                        <td className="py-2.5 px-2 text-center">
                          <Checkbox
                            checked={perm.editableByHr}
                            onCheckedChange={() => togglePerm(perm.fieldName, "editableByHr")}
                          />
                        </td>
                        <td className="py-2.5 px-2 text-center">
                          <Checkbox
                            checked={perm.editableByLineManager}
                            onCheckedChange={() => togglePerm(perm.fieldName, "editableByLineManager")}
                          />
                        </td>
                        <td className="py-2.5 px-2 text-center">
                          <Checkbox
                            checked={perm.editableByAdmin}
                            disabled
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ))}
        </>
        )}
      </div>
    </AppLayout>
  );
}
